import type { ReactNode } from 'react';
import { useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { EventSegmentType } from '@eti/schema-types';
import { Text } from '@etg/wings';
import type { ExtendedEventSegmentType } from '../models/models';
import StopoverInRomeContent from '../../../../../../widgets/trip-event/StopoverInRomeContent';

const bulletListStyles = css`
  list-style: unset;
  padding-inline-start: 24px;
  width: 100%;

  & li {
    padding-bottom: 4px;
    :last-child {
      padding-bottom: 0;
    }
  }
`;

interface TripEventContentProps {
  segment: ExtendedEventSegmentType;
  type: Exclude<EventSegmentType, typeof EventSegmentType.Stop>;
}

const ItineraryTripEventContent = ({ segment, type }: TripEventContentProps) => {
  const { t } = useTranslation();

  const EVENT_SEGMENT_TYPE_TO_DESCRIPTION: Record<
    Exclude<EventSegmentType, typeof EventSegmentType.Stop>,
    ReactNode
  > = {
    [EventSegmentType.OvernightStay]: t(
      'TripDetails.Details.Description.Overnight',
      segment.previousDestination,
    ),
    [EventSegmentType.ChangeOfAirport]: t(
      'TripDetails.Details.Description.ChangeOfAirport',
      segment.previousAirport,
      segment.nextAirport,
    ),
    [EventSegmentType.TransferToAirport]: t('TripDetails.Details.Description.TransferToAirport'),
    [EventSegmentType.TransferToBus]: t('TripDetails.Details.Description.TransferToBus'),
    [EventSegmentType.TransferToTrain]: t('TripDetails.Details.Description.TransferToTrain'),
    [EventSegmentType.SelfTransfer]: (
      <ul
        className={bulletListStyles}
        data-testid={`event-segment-${segment.segmentId}-self-transfer`}
      >
        <li>{t('SelfTransfer.Result.Baggage.Text')}</li>
        <li>{t('SelfTransfer.Result.Transfer.Text')}</li>
        <li>
          <Text asHtml>{t('SelfTransfer.Itinerary.Guarantee.Text')}</Text>
        </li>
      </ul>
    ),
    [EventSegmentType.ShortStopover]: (
      <StopoverInRomeContent type={EventSegmentType.ShortStopover} />
    ),
    [EventSegmentType.OneDayStopover]: (
      <StopoverInRomeContent type={EventSegmentType.OneDayStopover} />
    ),
    [EventSegmentType.OneNightStopover]: (
      <StopoverInRomeContent type={EventSegmentType.OneNightStopover} />
    ),
    [EventSegmentType.TwoNightsStopover]: (
      <StopoverInRomeContent type={EventSegmentType.TwoNightsStopover} />
    ),
  };

  return <>{EVENT_SEGMENT_TYPE_TO_DESCRIPTION[type]}</>;
};

export default ItineraryTripEventContent;
